import { v4 as uuidv4 } from "uuid";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AddIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  Flex,
  Grid,
  Select,
  Text,
} from "@chakra-ui/react";
import { useEarmold, useEarmoldVariants } from "../hooks/earmolds.hooks";
import { useParams } from "react-router-dom";
import { useVariant } from "../hooks/variants.hooks";
import { ReactNode, useEffect, useRef, useState } from "react";
import DotsGridIcon from "../components/icons/DotsGridIcon";
import TrashIcon from "../components/icons/TrashIcon";
import PrimaryButton from "../components/PrimaryButton";
import { useList } from "@uidotdev/usehooks";
import {
  VariantCondition,
  VariantConditionInnerOperator,
  VariantOption,
} from "../types/earmold.types";
import Input from "../components/inputs/Input";
import VariantForm, {
  VariantFormHandler,
} from "../components/forms/VariantForm";
import _ from "lodash";
import { updateVariant } from "../mutations/variant.mutations";
import { useMutation, useQueryClient } from "react-query";
import InputTags from "../components/inputs/InputTags";
import Spacer from "../components/Spacer";
import { useSimpleToast } from "../hooks/toast.hooks";

type SortableVStackProps = {
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setItems: (items: any[]) => void;
};

export function SortableItem({ id }: { id: string }) {
  const { attributes, listeners, setNodeRef } = useSortable({ id });

  return (
    <div className="list-row" ref={setNodeRef}>
      <span>{id}</span>
      <button {...attributes} {...listeners}>
        ⣿
      </button>
    </div>
  );
}

const SortableVStack = ({ children, items, setItems }: SortableVStackProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over !== null && active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.uuid === active.id);
      const newIndex = items.findIndex((item) => item.uuid === over?.id);
      setItems(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

export function ProductVariantsDetailPageSortableItem({
  id,
  item,
  error,
  setItem,
  removeItem,
}: {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setItem: (item: any) => void;
  removeItem: () => void;
}) {
  const { attributes, listeners, setNodeRef } = useSortable({
    id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  return (
    <div ref={setNodeRef}>
      {/* Line */}
      <Grid
        alignItems="center"
        gap={4}
        pt={1}
        templateColumns="20px 2fr 20rem 20rem 20px"
      >
        <DotsGridIcon {...attributes} {...listeners} color="#D0D5DD" />
        <Box>
          <Input
            defaultValue={item.label}
            onChange={(evt) => setItem({ ...item, label: evt.target.value })}
            name="label"
            placeholder="Label"
            error={error?.label != undefined}
          ></Input>
        </Box>
        <Input
          defaultValue={item.sageId}
          placeholder="ID Sage"
          onChange={(evt) => setItem({ ...item, sageId: evt.target.value })}
          name="sageId"
        ></Input>
        <Input
          type="number"
          placeholder="Tarif"
          unit="€"
          defaultValue={item.cost}
          onChange={(evt) => setItem({ ...item, cost: evt.target.value })}
          name="cost"
        ></Input>
        <TrashIcon onClick={removeItem}></TrashIcon>
      </Grid>

      {/* Errors */}
      <Grid
        alignItems="center"
        gap={4}
        pt={1}
        templateColumns="20px 2fr 20rem 20rem 20px"
      >
        <Box></Box>
        <Box>
          {error?.label && (
            <Text
              mt="4px"
              pb={3}
              color="rgb(229, 62, 62)"
              lineHeight="17px"
              fontSize="14px"
              fontWeight="400"
            >
              {error.label}
            </Text>
          )}
        </Box>
        <Box></Box>
        <Box></Box>
        <Box></Box>
      </Grid>
    </div>
  );
}

const ProductVariantsDetailPage = () => {
  const variantFormRef = useRef<VariantFormHandler>(null);
  const { id, sid } = useParams();
  const { data: earmold } = useEarmold(id);
  const { data: earmoldVariants } = useEarmoldVariants(id);
  const { data: variant } = useVariant(sid);
  const [
    options,
    { set: setOptions, updateAt: updateAtOptions, removeAt: removeAtOptions },
  ] = useList<VariantOption>([]);
  const [conditions, { push: pushCondition, set: setConditions }] =
    useList<VariantCondition>([]);
  useEffect(() => {
    if (variant != null && options.length === 0) {
      setOptions(variant.options.data ?? []);
      setConditions(variant.options.conditions ?? []);
    }
  }, [variant]);
  const [editVariantDetail, setEditVariantDetail] = useState<boolean>(false);
  const [optionsError, { set: setOptionsError }] = useList<{
    label?: string;
  } | null>([]);
  const [option, setOption] = useState<VariantOption>({
    uuid: uuidv4(),
    label: "",
    sageId: "",
    cost: 0,
  });
  const toast = useSimpleToast()
  const queryClient = useQueryClient();
  const { mutate: mutateUpdateVariant } = useMutation(updateVariant, {
    onSuccess: () => {
      setEditVariantDetail(false);
      queryClient.invalidateQueries("variant");
      toast.success('Sauvegarde', `Les modifications ont été enregistrées.`)
    },
    onError: () => {
      toast.error('Sauvegarde', `Les modifications n'ont pas pu être sauvegardées. Essayer plus tard.`)
    }
  });
  const handleSave = async () => {
    // 0. Validate input
    const variantValid = variantFormRef.current ? await variantFormRef.current.valid() : false;
    const tOptionsErrors = options.map((option) => {
      const optionError = {
        ...((option.label == null || option.label === "") && {
          label: "Ce champ est requis",
        }),
      };
      return Object.keys(optionError).length === 0 ? null : optionError;
    });
    setOptionsError(tOptionsErrors);
    const optionsValid = _.every(
      tOptionsErrors.map((optionError) => optionError == null)
    );
    // 2. Save variant
    let payload = {}
    if (variantValid && variantFormRef.current) {
      payload = {
        ...payload,
        label: variantFormRef.current.values().label,
        description: variantFormRef.current.values().description,
      }
    }
    if (optionsValid && options) {
      payload = {
        ...payload,
        options: {
          data: options.map((option) => ({
            cost: option.cost ?? 0,
            label: option.label ?? "",
            sageId: option.sageId ?? "",
            uuid: option.uuid,
          })),
          conditions,
        },
      }
    }
    mutateUpdateVariant({ id: sid, variant: payload });
  };
  const getPreviousVariants = ({ id }: { id: number }) => {
    if (earmoldVariants) {
      // 1. Find index
      const currentVariantIndex = earmoldVariants.findIndex((v) => v.id === id);
      // 2. If not on list return an empty list of elements
      if (currentVariantIndex === -1) {
        return [];
      }
      // 3. Otherwise map data to the expected shape
      return earmoldVariants.slice(0, currentVariantIndex).map((v) => ({
        label: v.label,
        value: v.id,
      }));
    }
    return [];
  };
  const getVariantOptions = ({ id }: { id: number | null }) => {
    if (id != null && earmoldVariants != null) {
      const variantIndex = earmoldVariants.findIndex((v) => v.id === id);
      if (variantIndex !== -1) {
        return earmoldVariants[variantIndex].options.data.map((o) => ({
          label: o.label,
          value: o.uuid,
          uuid: o.uuid,
        }));
      }
    }
    return [];
  };
  const translateUuidToLabel = ({ uuid, options }: { uuid: string, options: { label: string, uuid: string }[] }) => {
    const option = options.find(option => option.uuid === uuid);
    if (option) {
      return option.label;
    }
    return uuid
  }
  return (
    <>
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem>
          <BreadcrumbLink
            href="/products"
            color="gray.600"
            fontWeight={600}
            fontSize="14px"
          >
            Produits
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <BreadcrumbLink
            href={`/products/earmolds/${id}`}
            color="gray.600"
            fontWeight={600}
            fontSize="14px"
          >
            {earmold && earmold.label}
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <BreadcrumbLink
            href="#"
            color="brand.700"
            fontWeight={600}
            fontSize="14px"
          >
            {variant && variant.label}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* VARIANT DETAILS */}
      {editVariantDetail === true && (
        <Card mt={4} p={4}>
          <Text lineHeight="27px" fontSize="18px" fontWeight="600">
            {variant && variant.label}
          </Text>

          <VariantForm
            ref={variantFormRef}
            value={variant ?? undefined}
          ></VariantForm>

          <Text
            lineHeight="18px"
            fontSize="12px"
            fontWeight="500"
            color="gray.500"
            mb={4}
          >
            Valeurs de l'option
          </Text>

          {options && (
            <SortableVStack items={options} setItems={setOptions}>
              {options.map((option, index) => (
                <ProductVariantsDetailPageSortableItem
                  key={option.uuid}
                  id={option.uuid}
                  item={option}
                  setItem={(item: VariantOption) =>
                    updateAtOptions(index, item)
                  }
                  removeItem={() => removeAtOptions(index)}
                  error={
                    optionsError && index < optionsError.length
                      ? optionsError[index]
                      : {}
                  }
                />
              ))}
            </SortableVStack>
          )}
          <Grid
            alignItems="center"
            gap={4}
            pt={4}
            templateColumns="20px 2fr 20rem 20rem 20px"
          >
            <DotsGridIcon></DotsGridIcon>
            <Input
              placeholder="Ajouter une autre valeur"
              defaultValue={option.label}
              onChange={(evt) => {
                setOption({ ...option, label: evt.target.value });
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.code === "Enter") {
                  // 1. Add option to options
                  setOptions([...(options ?? []), { ...option }]);
                  // 2. Reset option fields
                  setOption({
                    uuid: uuidv4(),
                    label: "",
                    sageId: "",
                    cost: 0,
                  });
                }
              }}
              name={"label"}
            ></Input>
            <Input
              placeholder="ID Sage"
              defaultValue={option.sageId ?? ""}
              onChange={(evt) => {
                setOption({ ...option, sageId: evt.target.value });
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.code === "Enter") {
                  // 1. Add option to options
                  setOptions([...(options ?? []), { ...option }]);
                  // 2. Reset option fields
                  setOption({
                    uuid: uuidv4(),
                    label: "",
                    sageId: "",
                    cost: 0,
                  });
                }
              }}
              name={"label"}
            ></Input>
            <Input
              placeholder="Tarif"
              unit="€"
              type="number"
              defaultValue={option.cost}
              onChange={(evt) => {
                setOption({
                  ...option,
                  cost: Number.parseFloat(evt.target.value),
                });
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.code === "Enter") {
                  // 1. Add option to options
                  setOptions([...(options ?? []), { ...option }]);
                  // 2. Reset option fields
                  setOption({
                    uuid: uuidv4(),
                    label: "",
                    sageId: "",
                    cost: 0,
                  });
                }
              }}
              name={"label"}
            ></Input>
            <Box></Box>
            <Box></Box>
          </Grid>

          <PrimaryButton w="fit-content" mt={4} onClick={handleSave}>
            Sauvegarder
          </PrimaryButton>
        </Card>
      )}
      {editVariantDetail === false && (
        <Card mt={4} p={4}>
          <Flex justifyContent="space-between">
            <Text lineHeight="27px" fontSize="18px" fontWeight="600">
              {variant && variant.label}
            </Text>
            <Button w="fit-content" onClick={() => setEditVariantDetail(true)}>
              Modifier
            </Button>
          </Flex>

          <Flex gap={4} mt={4}>
            {variant?.options.data.map((data) => (
              <Box
                w="fit-content"
                border="solid 1px"
                borderColor="gray.300"
                borderRadius="8px"
                px={2}
              >
                {data.label}
              </Box>
            ))}
          </Flex>
        </Card>
      )}

      {/* CONDITIONS */}
      <Card mt={4} p={4}>
        <Flex direction="column">
          <Text lineHeight="27px" fontSize="18px" fontWeight="600" mb={4}>
            Logiques conditionnelles
          </Text>

          {conditions &&
            conditions.map((condition, index) => (
              <>
                {index !== 0 && (
                  <Box position="relative" my={4}>
                    <hr></hr>
                    <Text
                      color="gray.400"
                      position="absolute"
                      top="-13px"
                      left="10px"
                      backgroundColor="white"
                      px="10px"
                      zIndex={3}
                    >
                      ou
                    </Text>
                  </Box>
                )}
                <Box>
                  {/* VARIANT OPTION SELECTION */}
                  <Grid templateColumns={"4em 1fr"} alignItems="center">
                    <Text>Pour</Text>
                    <InputTags
                      my={3}
                      options={options.map((option) => ({
                        label: option.label,
                        value: option.uuid,
                      }))}
                      selected={condition.targets.map((t) => ({
                        label: translateUuidToLabel({ uuid: t, options }),
                        value: t,
                      }))}
                      setSelected={(selected) => {
                        const conditionsCopy = [...conditions];
                        if (index < conditionsCopy.length) {
                          conditionsCopy[index].targets = selected.map(
                            (s) => s.value
                          );
                          setConditions(conditionsCopy);
                        }
                      }}
                    ></InputTags>
                  </Grid>
                  {/* CONDITIONS SELECTION */}
                  {condition.conditions &&
                    condition.conditions.map((subcondition, subindex) => (
                      <Grid
                        templateColumns={"4em 1fr 1fr 2fr 40px"}
                        alignItems="center"
                      >
                        <Text>{subindex === 0 ? "Si" : "et si"}</Text>
                        <Box mr={4}>
                          <Select
                            value={subcondition.variantId ?? undefined}
                            onChange={(evt) => {
                              const conditionsCopy = [...conditions];
                              if (
                                index < conditionsCopy.length &&
                                subindex <
                                  conditionsCopy[index].conditions.length
                              ) {
                                const value = Number.parseInt(evt.target.value);
                                conditionsCopy[index].conditions[
                                  subindex
                                ].variantId = Number.isNaN(value)
                                  ? null
                                  : value;
                                setConditions(conditionsCopy);
                              }
                            }}
                          >
                            <option value={undefined}>---</option>
                            {variant &&
                              getPreviousVariants({ id: variant.id }).map(
                                (pv) => (
                                  <option value={pv.value}>{pv.label}</option>
                                )
                              )}
                          </Select>
                        </Box>
                        <Box mr={4}>
                          <Select
                            value={subcondition.operator}
                            onChange={(evt) => {
                              const conditionsCopy = [...conditions];
                              if (
                                index < conditionsCopy.length &&
                                subindex <
                                  conditionsCopy[index].conditions.length
                              ) {
                                conditionsCopy[index].conditions[
                                  subindex
                                ].operator = evt.target
                                  .value as VariantConditionInnerOperator;
                                setConditions(conditionsCopy);
                              }
                            }}
                          >
                            <option value="==">Est égal</option>
                            <option value="!=">N'est pas égal</option>
                          </Select>
                        </Box>
                        <InputTags
                          my={3}
                          options={getVariantOptions({
                            id: subcondition.variantId,
                          })}
                          selected={subcondition.values.map((v) => ({
                            label: translateUuidToLabel({ uuid: v, options: getVariantOptions({ id: subcondition.variantId }) }),
                            value: v,
                          }))}
                          setSelected={(selected) => {
                            const conditionsCopy = [...conditions];
                            if (
                              index < conditionsCopy.length &&
                              subindex < conditionsCopy[index].conditions.length
                            ) {
                              conditionsCopy[index].conditions[
                                subindex
                              ].values = selected.map((s) => s.value);
                              setConditions(conditionsCopy);
                            }
                          }}
                        ></InputTags>
                        <Flex justifyContent="end" alignItems="center">
                          <TrashIcon
                            onClick={() => {
                              const conditionsCopy = [...conditions];
                              if (
                                index < conditionsCopy.length &&
                                subindex <
                                  conditionsCopy[index].conditions.length
                              ) {
                                conditionsCopy[index].conditions.splice(
                                  subindex,
                                  1
                                );
                                setConditions(conditionsCopy);
                              }
                            }}
                          ></TrashIcon>
                        </Flex>
                      </Grid>
                    ))}
                  {/* ADD CONDITION LINK */}
                  <Grid templateColumns={"4em 1fr"} alignItems="center" my={4}>
                    <Box
                      onClick={() => {
                        const conditionsCopy = [...conditions];
                        if (index < conditionsCopy.length) {
                          conditionsCopy.splice(index, 1);
                          setConditions(conditionsCopy);
                        }
                      }}
                    >
                      <TrashIcon></TrashIcon>
                    </Box>
                    <Button
                      variant="link"
                      w="fit-content"
                      color="brand.700"
                      onClick={() => {
                        const conditionsCopy = [...conditions];
                        if (index < conditionsCopy.length) {
                          conditionsCopy[index].conditions.push({
                            variantId: null,
                            operator: VariantConditionInnerOperator.EQUAL,
                            values: [],
                          });
                          setConditions(conditionsCopy);
                        }
                      }}
                    >
                      <AddIcon mr={4}></AddIcon>Ajouter une condition
                    </Button>
                  </Grid>
                </Box>
              </>
            ))}

          <Spacer my={4}></Spacer>

          <Flex justify="space-between">
            <Button
              variant="link"
              w="fit-content"
              color="brand.700"
              onClick={() =>
                pushCondition({
                  targets: [],
                  conditions: [
                    {
                      variantId: null,
                      values: [],
                      operator: VariantConditionInnerOperator.EQUAL,
                    },
                  ],
                })
              }
            >
              <AddIcon mr={4}></AddIcon>Ajouter des logiques
            </Button>

            <Button variant="solid" bgColor="brand.700" color="white" onClick={handleSave}>
              Sauvegarder
            </Button>
          </Flex>
        </Flex>
      </Card>
    </>
  );
};

export default ProductVariantsDetailPage;
